import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, DialogContent, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ContentButton } from 'src/layouts/form/FormButton';
import { downloadApi } from 'src/service';
import { getFileType, getOriginalFileName } from 'src/utils/fileUploadUtils';

// 허용된 파일 타입 정의
const ACCEPTED_FILE_TYPES = {
  'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
  'application/pdf': ['.pdf'],
  'application/zip': ['.zip'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
};

export interface IDownladProps {
  url: string;
  sid: number;
  filePath: string[];
  isZipFile?: boolean;
}
interface IDownloadDialogProps {
  data: IDownladProps;
}

export const CDownloadDialog = observer(({ data }: IDownloadDialogProps) => {
  const { t } = useTranslation();

  // 지원서 다운로드
  const handleDownload = async (fileNm: string, index: number) => {
    const isZipFile = getFileType(data.filePath[index], ACCEPTED_FILE_TYPES).type === 'zip';
    try {
      const attachment = {
        url: data.url,
        sid: data.sid,
        fileNm: fileNm,
        filePath: data.filePath[index],
        isZipFile: isZipFile,
      };
      await downloadApi.downloadFile(attachment);
    } catch (error) {
      console.error(error);
    }
  };

  // zip 파일 다운로드
  const handleZipDownload = async (fileNm: string) => {
    try {
      await downloadApi.downloadZipFile(data.url, fileNm);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      {data.filePath.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, paddingRight: 3 }}>
          {/* 파일 1개 이상일 경우 다중 파일 zip 다운로드 버튼 추가 */}
          <ContentButton
            onClick={async () =>
              await handleZipDownload(
                `${getOriginalFileName(data.filePath[0])} 외 ${data.filePath.length - 1}개`,
              )
            }
          >
            <DownloadIcon style={{ width: 18, height: 18, marginRight: 1 }} />
            {t('common.button.download(zip)')}
          </ContentButton>
        </Box>
      )}
      <DialogContent dividers sx={{ pt: '10px' }}>
        {data.filePath.map((filePath, index) => {
          return (
            <DownloadItemContainer
              key={index}
              sx={{ cursor: 'pointer' }}
              onClick={() => handleDownload(getOriginalFileName(filePath), index)}
            >
              <Text>{getOriginalFileName(filePath)}</Text>
              <DownloadIcon style={{ width: 18, height: 18 }} />
            </DownloadItemContainer>
          );
        })}
      </DialogContent>
    </>
  );
});

const DownloadItemContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 2,
  justifyContent: 'space-between',
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(2),
  alignItems: 'center',
  cursor: 'pointer',
}));
const Text = styled('a')(() => ({
  cursor: 'pointer',
  fontSize: '16px',
  lineHeight: '16px',
  fontWeight: 600,
}));
